import { memo, useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  ChatCustomFilter,
  ChatFilterOperator,
  type RecordFilter,
  type AgentDataSource,
  type AgentFilter,
  type CustomFilter,
} from '@trinity/chat';
import {
  BiChatContextType,
  BrandInsightChatContexts,
  ChatContextType,
  isBrandContext,
  isEverythingContext,
  isFolderContext,
  isProjectContext,
} from '@trinity/chat';
import {
  ActiveChatSessionContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { useAgentResources } from '@trinity/chat-components/components/Chat.Header/AgentResources.Container.js';
import { HeaderContextContext } from '@trinity/chat-components/components/Chat.Header/context';
import { ContextSearch } from '@trinity/chat-components/components/Chat.Header/ContextSearch';
import { ChatContextSearchContainer } from '@trinity/chat-components/components/Chat.Header/ContextSearch.Container';
import { SimpleContextSelector } from '@trinity/chat-components/components/Chat.Header/SimpleContextSelector';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import {
  generateEmptyChatSessionFiltersState,
  getChatSourceFilterOptions,
} from '@trinity/chat-components/components/Chat.State/state.initial-state';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { GroupFeaturesContext, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { AgentCustomFilterValueSelector } from './AgentCustomFilter.Value';
import { AgentCustomFilters } from './AgentCustomFilters';
import { DataSource } from './DataSource';
import type { AgentFilterItem } from './interfaces';
import { FilterTypeEnum } from './interfaces';
import * as Presentation from './Presentation';
import { Project } from './Project';
import { PublishedDate } from './PublishedDate';
import { Record } from './Record';
import { Source } from './Source';
import { Tag } from './Tag';

type Props = {
  active: boolean;
};

export function FilterPaneWorkspace(props: Props) {
  const agentResources = useAgentResources();
  const { isDigitalTwin, interactionMode } = useAvatarPanel();
  const { context } = useContext(ActiveChatSessionContext);
  const isDTVideo = isDigitalTwin && interactionMode === 'video';
  const shouldShowStandardFilters = useMemo(() => {
    return context.type === ChatContextType.Agent && BrandInsightChatContexts.includes(context.data.identifier);
  }, [context]);

  if (agentResources.isLoading) {
    return null;
  }

  return (
    <Root>
      <Header>
        <HeaderLabel>Source Filters</HeaderLabel>
        <ResetController {...props} />
      </Header>
      {isDTVideo && <DTContextSection />}
      <Filters>
        {shouldShowStandardFilters && (
          <>
            <DataSourceController dataSources={agentResources.data.dataSources} />
            <PublishedDateController />
            <ProjectController />
            <TagController />
            <SourceController />
            <RecordController />
          </>
        )}
        <UserTerritoriesController filters={agentResources.data.filters} />
        <AgentCustomFiltersController filters={agentResources.data.filters} />
      </Filters>
    </Root>
  );
}

function DTContextSection() {
  const headerCtx = useContext(HeaderContextContext);
  const hasActiveSearch = !!headerCtx?.pendingContextType || !!headerCtx?.pendingEverythingSearch;

  return (
    <DTSection>
      <DTSectionLabel>Context</DTSectionLabel>
      <DTSectionContent>
        <SimpleContextSelector />
        <ChatContextSearchContainer>
          {hasActiveSearch ? <StyledContextSearch /> : null}
        </ChatContextSearchContainer>
      </DTSectionContent>
    </DTSection>
  );
}

function ProjectController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  const enabled = useMemo(() => {
    if (isProjectContext(context)) {
      return false;
    }

    if (isBrandContext(context)) {
      return true;
    }
    if (isFolderContext(context)) {
      return true;
    }
    if (isEverythingContext(context)) {
      return true;
    }

    return false;
  }, [context]);

  const value = useMemo(() => {
    if (context.type === ChatContextType.Project) {
      return [{ id: context.data.projectId, name: context.metadata.name }];
    }
    if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Project) {
      return [{ id: context.data.subject.id as number, name: context.data.subject.name }];
    }

    return filters.projects;
  }, [context, filters.projects]);

  return (
    <Project
      disabled={!enabled}
      context={context}
      value={value}
      onChange={(projects) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              projects,
            },
          },
        });
      }}
    />
  );
}

function RecordController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const session = useContext(ActiveChatSessionContext);
  const features = useContext(GroupFeaturesContext);
  const hasProfilesEnabled = features.hcpProfiles;

  const value = useMemo(() => {
    return filters.records?.length ? filters.records[0] : null;
  }, [filters.records]);

  const handleChange = useCallback(
    (data: RecordFilter.Item) => {
      dispatch({
        type: 'filters/updated',
        payload: {
          filters: {
            records: [data],
          },
        },
      });
    },
    [dispatch]
  );

  if (!hasProfilesEnabled) {
    return null;
  }

  if (session.context.type !== ChatContextType.Global) {
    return null;
  }

  return <Record onChange={handleChange} value={value} />;
}

function AgentCustomFiltersController({ filters }: { filters?: AgentFilter[] }) {
  const session = useContext(ActiveChatSessionContext);

  if (session.context.type !== ChatContextType.Agent) {
    return null;
  }

  return <AgentCustomFilters context={session.context} filters={filters} />;
}

type AgentFilterValueWithMetadata = CustomFilter.ItemValue & {
  filterId?: number | string;
};

function normalizeSubjectKind(kind: string | undefined): string {
  return kind?.trim().toLowerCase() ?? '';
}

function UserTerritoriesController({ filters }: { filters?: AgentFilter[] }) {
  const session = useContext(ActiveChatSessionContext);
  const sessionFilters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const isTerritorySubjectActive = useMemo(() => {
    if (session.context.type !== ChatContextType.Agent) {
      return false;
    }

    return normalizeSubjectKind(session.context.data.subject?.kind) === 'territory';
  }, [session.context]);
  const customFilters = useMemo(() => {
    return sessionFilters.custom || [];
  }, [sessionFilters.custom]);

  const territoryFilter = useMemo<AgentFilterItem | null>(() => {
    const foundFilter = filters?.find((filter) => filter.type === FilterTypeEnum.UserTerritory);
    if (!foundFilter) {
      return null;
    }

    return {
      id: foundFilter.id,
      identifier: foundFilter.identifier,
      label: foundFilter.label,
      type: FilterTypeEnum.UserTerritory,
      operators: foundFilter.operators,
      data: foundFilter.data,
    };
  }, [filters]);

  const territoryFilterIndex = useMemo(() => {
    if (!territoryFilter) {
      return -1;
    }

    return customFilters.findIndex((filter) => {
      if (filter.type !== ChatCustomFilter.AgentFilter) {
        return false;
      }

      const firstValue = filter.value[0] as AgentFilterValueWithMetadata | undefined;
      if (!firstValue?.filterId) {
        return false;
      }

      return Number(firstValue.filterId) === territoryFilter.id;
    });
  }, [customFilters, territoryFilter]);

  const value = useMemo(() => {
    if (territoryFilterIndex === -1) {
      return [];
    }

    const existingFilter = customFilters[territoryFilterIndex];
    if (!existingFilter || existingFilter.type !== ChatCustomFilter.AgentFilter) {
      return [];
    }

    return existingFilter.value;
  }, [customFilters, territoryFilterIndex]);

  const handleValuesChange = useCallback((values: CustomFilter.ItemValue[]) => {
    if (isTerritorySubjectActive) {
      return;
    }

    if (!territoryFilter) {
      return;
    }

    const singleValue = values[0] ? [values[0]] : [];

    if (singleValue.length === 0) {
      if (territoryFilterIndex !== -1) {
        dispatch({
          type: 'filters/remove-custom-filter',
          payload: {
            id: territoryFilterIndex,
          },
        });
      }
      return;
    }

    const territoryCustomFilter: CustomFilter.AgentCustomFilter = {
      type: ChatCustomFilter.AgentFilter,
      operator: [ChatFilterOperator.Eq],
      value: singleValue.map((item) => ({
        ...item,
        filterId: territoryFilter.id,
        filterLabel: territoryFilter.label,
      })),
    };

    if (territoryFilterIndex === -1) {
      dispatch({
        type: 'filters/add-custom-filter',
        payload: {
          value: territoryCustomFilter,
        },
      });
      return;
    }

    dispatch({
      type: 'filters/update-custom-filter',
      payload: {
        id: territoryFilterIndex,
        value: territoryCustomFilter,
      },
    });
  }, [dispatch, isTerritorySubjectActive, territoryFilter, territoryFilterIndex]);

  if (session.context.type !== ChatContextType.Agent) {
    return null;
  }

  if (!territoryFilter) {
    return null;
  }

  return (
    <Presentation.Row>
      <Presentation.Label>{territoryFilter.label}</Presentation.Label>
      <Presentation.Value>
        <AgentCustomFilterValueSelector
          agentIdentifier={session.context.data.agent.identifier}
          filter={territoryFilter}
          values={value}
          onValuesChange={handleValuesChange}
          readOnly={isTerritorySubjectActive}
        />
      </Presentation.Value>
    </Presentation.Row>
  );
}

function SourceController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();

  const options = useMemo(() => getChatSourceFilterOptions({ context, features }), [context, features]);

  if (!options.length) return null;

  return (
    <Source
      options={options}
      value={filters.sources}
      onChange={(sources) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              sources: sources.length ? sources : options,
            },
          },
        });
      }}
    />
  );
}

function PublishedDateController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const groupFeatures = useContext(GroupFeaturesContext);

  const defaultValue = useMemo(
    () =>
      generateEmptyChatSessionFiltersState({
        context,
        features: groupFeatures,
      }).minDocumentDate,
    [context, groupFeatures]
  );

  return (
    <PublishedDate
      defaultValue={defaultValue}
      value={filters.minDocumentDate}
      onChange={(minDocumentDate) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              minDocumentDate,
            },
          },
        });
      }}
    />
  );
}

function TagController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  return (
    <Tag
      context={context}
      value={filters.tags}
      onChange={(tags) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              tags,
            },
          },
        });
      }}
    />
  );
}

function DataSourceController({ dataSources }: { dataSources?: AgentDataSource[] }) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  const shouldRenderFilter = useMemo(() => {
    if (context.type === ChatContextType.Agent) {
      return !BrandInsightChatContexts.includes(context.data.identifier);
    }
    return true;
  }, [context]);

  const transformedDataSources = useMemo(() => {
    if (!dataSources) return undefined;
    return dataSources.map((ds) => ({
      id: String(ds.id),
      name: ds.name,
    }));
  }, [dataSources]);

  const options = transformedDataSources || filters.dataSources;

  if (!shouldRenderFilter) {
    return null;
  }

  return (
    <DataSource
      context={context}
      options={options}
      value={filters.dataSources}
      dataSources={dataSources}
      onChange={(values) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              dataSources: values.length ? values : options,
            },
          },
        });
      }}
    />
  );
}

const ResetController = memo(({ active }: Props) => {
  const dispatch = useContext(ChatStateDispatchContext);

  return (
    <HeaderReset disabled={!active} onClick={() => dispatch({ type: 'filters/reset' })}>
      Reset
    </HeaderReset>
  );
});

const Root = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: 5,
  border: `1px solid ${theme.palette.gray.light1}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  padding: 12,
  minWidth: 450,
  maxWidth: 600,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const HeaderReset = styled.button(({ disabled, theme }) => ({
  color: !disabled ? theme.palette.text.primary : theme.palette.gray.light1,
  fontSize: '14px',
  transition: 'text-decoration 150ms, color 150ms',

  ':hover': {
    textDecoration: disabled ? 'underline' : 'none',
  },
}));

const Filters = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const DTSection = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.gray.light2}`,
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
  padding: '10px 2px',
  marginBottom: '10px',
}));

const DTSectionLabel = styled.div(({ theme }) => ({
  fontSize: '12px',
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.main,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '8px',
}));

const DTSectionContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

const StyledContextSearch = styled(ContextSearch)({});
